import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  TwitterAuthProvider,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, db } from "./Firebase-Config";
import { collection, doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";

const UserContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [pending, setPending] = useState(true); 
  const [error, setError] = useState(false);


const createUser = async (email, password) => {
  try {

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const newUser = userCredential.user;        
    const settingsRef = collection(db, "users");

    setDoc(
      doc(settingsRef, newUser.uid),
      {
        uuid: newUser.uid,
        status: "user",
        name_update: serverTimestamp(),
        created: newUser.metadata.creationTime,
        log: newUser.metadata.lastSignInTime
      },
      { merge: true }
    ).catch((fsError) => {
      console.error("Failed to initialize user document in Firestore:", fsError);
    });

    sendEmailVerification(newUser)
      .then(() => console.log("Verification email dispatched."))
      .catch((emailError) => {
        console.error("Verification email failed to send, but account was created:", emailError);
      });

    return newUser; 
  } catch (authError) {
    setError(authError.message);
    console.error("Auth creation failed:", authError.message);
    throw authError;
  }
};

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (e) {
      setError(e.message);
      console.error(e.message);
      throw e;
    }
  };

  const login = (email, password) => {
    try {
      setPending(true);
      return signInWithEmailAndPassword(auth, email, password).then(() => {
        setPending(false);
      });
    } catch (e) {
      setError(e.message);
      setPending(false);
      console.log(e.message, error);
    }
  };

  const googleSignIn = () => {
    const provider = new GoogleAuthProvider();
    try {
      setPending(true);
      return signInWithPopup(auth, provider)
        .then(async (result) => {
          const user = result.user; 
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            setPending(false); 
            return;
          } else {
            const { uid } = auth.currentUser;        
            const settingsRef = collection(db, "users");

            await setDoc(
              doc(settingsRef, uid),
              {
                uuid: uid,
                status: "user",
                name_update: serverTimestamp(),
                created: auth.currentUser.metadata.creationTime,
                log: auth.currentUser.metadata.lastSignInTime,
                email_verified: auth.currentUser.emailVerified,
                isAnonymous: auth.currentUser.isAnonymous,
              },
              { merge: true }
            );
            setPending(false);
          }
        }).catch((error) => {
          setPending(false);
          console.error(error);
        });
    } catch (error) {
      setPending(false);
    }
  }

  const twitterSignIn = () => {
    const provider = new TwitterAuthProvider();
    try {
      setPending(true);
      return signInWithPopup(auth, provider)
        .then(async (result) => {
          const user = result.user; 
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            setPending(false);
            console.log("Document data:", docSnap.data(), user);
            return;
          } else {
            const { uid } = auth.currentUser;        
            const settingsRef = collection(db, "users");

            await setDoc(
              doc(settingsRef, uid),
              {
                uuid: uid,
                photo: auth.currentUser.photoURL,
                name: auth.currentUser.displayName,
                email: auth.currentUser.email,
                status: "user",
                name_update: serverTimestamp(),
                created: auth.currentUser.metadata.creationTime,
                log: auth.currentUser.metadata.lastSignInTime,
                email_verified: auth.currentUser.emailVerified,
                isAnonymous: auth.currentUser.isAnonymous,
              },
              { merge: true }
            );
            setPending(false);
          }
        }).catch((error) => {
          setPending(false);
          console.error(error);
        });
    } catch (error) {
      setPending(false);
    }
  }

  const logout = async () => {
    try {
      setPending(true);
      await signOut(auth);
      setPending(false);
      navigate("/"); 
    } catch (e) {
      setError(e.message);
      setPending(false);
      console.log(e.message, error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        setUser(null);
      }
      setPending(false); 
    });

    return () => unsubscribe();
  }, []);

  if (pending) {
    return <>Loading...</>;
  }

  return (
    <UserContext.Provider
      value={{ 
        createUser, 
        resetPassword,
        user, 
        logout, 
        login, 
        googleSignIn, 
        twitterSignIn, 
        pending, 
        setPending 
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserAuth = () => {
  return useContext(UserContext);
};
